import React, { useCallback, useRef } from 'react'
import noop from 'lib/function/noop'
import Group from 'components/utils/Group'
import { OfferListSortOption } from 'constants/offerListFilters'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import TourSearchFilterContent, { TourSearchFilterContentElement } from 'components/Tours/TourSearchFilterContent'

interface Option {
  value: string;
  label?: string;
  count: number;
}

export function generateOptionItems(
  options: Array<Option>,
  localStates: Set<string>,
) {
  return options.map(({ value, label, count }) => ({
    label: (
      <Group direction="horizontal" horizontalAlign="space-between">
        {label ?? value}
        <span>({count})</span>
      </Group>
    ),
    value,
    checked: localStates.has(value),
  }))
}

interface RangeFilter {
  min: string | undefined;
  max: string | undefined;
}

export type TourSearchPageFilterSubmit = {
  operators: Array<string>;
  categories: Array<string>;
  productTypes: Array<string>;
  price: { start: number; end: number };
  duration: { start: number; end: number };
  numberOfPlacesVisited?: RangeFilter;
  destinations: Array<string>;
  groupTypes: Array<string>;
  offerTags: Array<string>;
  luxPlusFeatures: Array<string>;
  sortBy?: string;
  tourStyles?: Array<string>;
};

interface Props {
  open?: boolean;
  onClose?: () => void;
  onFilterSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  searchFacetsState?: App.StatefulData<Tours.TourSearchFacets>;
  initialFacets?: Tours.TourSearchFacets;
  existingFilters?: App.OfferListFilters;
  sortOptions?: Array<OfferListSortOption>;
}

function TourSearchPageFilters(props: Props) {
  const {
    open,
    onClose,
    existingFilters,
    onFilterSubmit,
    sortOptions,
    searchFacetsState,
    initialFacets,
  } = props

  const tourSearchFilter = useRef<TourSearchFilterContentElement>(null)

  const onFormReset = useCallback(() => {
    tourSearchFilter.current?.onFormReset()
  }, [tourSearchFilter])

  const sortAndFiltersText =
    sortOptions && sortOptions.length > 0 ? 'Sort & Filter' : 'Filters'

  return (
    <ModalBase mode="drawer" isOpen={!!open} onClose={onClose}>
      <form onSubmit={onFilterSubmit}>
        <ModalHeader title={sortAndFiltersText} onCloseButtonClick={onClose} />
        <ModalBody>
          <ModalContent>
            <TourSearchFilterContent
              ref={tourSearchFilter}
              modalView
              searchFacetsState={searchFacetsState}
              existingFilters={existingFilters}
              initialFacets={initialFacets}
              sortOptions={sortOptions}
            />
          </ModalContent>
        </ModalBody>
        <ModalFooter
          primaryActionProps={{
            children: 'View offers',
            type: 'submit',
            'data-testid': 'view-offers-filter-button',
          }}
          secondaryActionProps={{
            children: 'Reset all',
            onClick: onFormReset,
          }}
        />
      </form>
    </ModalBase>
  )
}

TourSearchPageFilters.defaultProps = {
  onApply: noop,
  sortOptions: [],
}

export default TourSearchPageFilters
